var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var RestAuthCredentials_1;
import { JSON, JsonDiscriminatorValue, JsonSchemaV2 } from '@symon-ai/wisepipe-serialize';
import { Credentials, CredentialsType } from '@symon-ai/wisepipe-common';
import { restAuthCredentialsSchemaValidation } from '../../validations.generated';
export var RestAuthCredentialsType;
(function (RestAuthCredentialsType) {
    RestAuthCredentialsType["None"] = "none";
    RestAuthCredentialsType["Basic"] = "basic";
    RestAuthCredentialsType["ApiKey"] = "api_key";
    RestAuthCredentialsType["OAuthClientCredentials"] = "oauth_client_credentials";
    RestAuthCredentialsType["OAuthClientCode"] = "oauth_client_code"; // OAuth2 (clientID, clientSecret, refreshToken, accessToken)
})(RestAuthCredentialsType || (RestAuthCredentialsType = {}));
export const restAuthCredentialsSchema = {
    $id: 'urn:wisepipe-common-connection:RestAuthCredentials',
    type: 'object',
    properties: {
        type: {
            type: 'string',
            enum: Object.values(RestAuthCredentialsType)
        },
        userName: { type: 'string' },
        password: { type: 'string' },
        authHeader: { type: 'string' },
        accessToken: { type: 'string' }, // OAuth2
        refreshToken: { type: 'string' }, // OAuth2
        clientID: { type: 'string' }, // OAuth2
        clientSecret: { type: 'string' }, // OAuth2
        tokenUrl: { type: 'string' }, // OAuth2 uses auth code to get OAuth2 tokens
        tokenType: { type: 'string' },
        key: { type: 'string' },
        secret: { type: 'string' },
        authUrl: { type: 'string' }, // OAuth2 frontend calls this URL to get an auth code
        code: { type: 'string' }, // OAuth2 single use code returned by third-party
        redirectUri: { type: 'string' } // Many third-party OAuth2 require the original redirect URI for all subsequent transactions
    },
    required: ['type'],
    allOf: [
        {
            if: { properties: { type: { const: 'basic' } } },
            then: { required: ['userName', 'password'] }
        },
        {
            if: { properties: { type: { const: 'api_key' } } },
            then: { required: ['authHeader'] }
        },
        {
            // OAuth1
            if: { properties: { type: { const: 'oauth_client_credentials' } } },
            then: { required: ['tokenType', 'key', 'secret', 'authUrl'] }
        },
        {
            // OAuth2
            if: { properties: { type: { const: 'oauth_client_code' } } },
            then: { required: ['clientID', 'clientSecret', 'tokenUrl', 'authUrl', 'redirectUri'] }
        }
    ]
};
let RestAuthCredentials = RestAuthCredentials_1 = class RestAuthCredentials extends Credentials {
    constructor(type, userName, password, authHeader, accessToken, refreshToken, clientID, clientSecret, tokenUrl, tokenType, key, secret, authUrl, code, redirectUri) {
        super(CredentialsType.Rest);
        this.type = type;
        if (userName !== undefined)
            this.userName = userName;
        if (password !== undefined)
            this.password = password;
        if (authHeader !== undefined)
            this.authHeader = authHeader;
        if (accessToken !== undefined)
            this.accessToken = accessToken;
        if (refreshToken !== undefined)
            this.refreshToken = refreshToken;
        if (clientID !== undefined)
            this.clientID = clientID;
        if (clientSecret !== undefined)
            this.clientSecret = clientSecret;
        if (tokenUrl !== undefined)
            this.tokenUrl = tokenUrl;
        if (tokenType !== undefined)
            this.tokenType = tokenType;
        if (key !== undefined)
            this.key = key;
        if (secret !== undefined)
            this.secret = secret;
        if (authUrl !== undefined)
            this.authUrl = authUrl;
        if (code !== undefined)
            this.code = code;
        if (redirectUri !== undefined)
            this.redirectUri = redirectUri;
        JSON.validate(this, RestAuthCredentials_1);
    }
    isOAuth2CredentialsType() {
        // Only true for OAuthClientCode (OAuth2). False for: None, Basic, API Key, and OAuthClientCredentials (OAuth1).
        return this.type === RestAuthCredentialsType.OAuthClientCode;
    }
    async encrypt(encryptFn) {
        let password;
        let authHeader;
        let accessToken;
        let refreshToken;
        let clientSecret;
        let secret;
        if (this.password !== undefined) {
            password = await encryptFn(this.password);
        }
        if (this.authHeader !== undefined) {
            authHeader = await encryptFn(this.authHeader);
        }
        if (this.accessToken !== undefined) {
            accessToken = await encryptFn(this.accessToken);
        }
        if (this.refreshToken !== undefined) {
            refreshToken = await encryptFn(this.refreshToken);
        }
        if (this.clientSecret !== undefined) {
            clientSecret = await encryptFn(this.clientSecret);
        }
        if (this.secret !== undefined) {
            secret = await encryptFn(this.secret);
        }
        return new RestAuthCredentials_1(this.type, this.userName, password, authHeader, accessToken, refreshToken, this.clientID, clientSecret, this.tokenUrl, this.tokenType, this.key, secret, this.authUrl, this.code, this.redirectUri);
    }
    async decrypt(decryptFn) {
        let password;
        let authHeader;
        let accessToken;
        let refreshToken;
        let clientSecret;
        let secret;
        if (this.password !== undefined) {
            password = await decryptFn(this.password);
        }
        if (this.authHeader !== undefined) {
            authHeader = await decryptFn(this.authHeader);
        }
        if (this.accessToken !== undefined) {
            accessToken = await decryptFn(this.accessToken);
        }
        if (this.refreshToken !== undefined) {
            refreshToken = await decryptFn(this.refreshToken);
        }
        if (this.clientSecret !== undefined) {
            clientSecret = await decryptFn(this.clientSecret);
        }
        if (this.secret !== undefined) {
            secret = await decryptFn(this.secret);
        }
        return new RestAuthCredentials_1(this.type, this.userName, password, authHeader, accessToken, refreshToken, this.clientID, clientSecret, this.tokenUrl, this.tokenType, this.key, secret, this.authUrl, this.code, this.redirectUri);
    }
};
RestAuthCredentials = RestAuthCredentials_1 = __decorate([
    JsonSchemaV2(restAuthCredentialsSchema, restAuthCredentialsSchemaValidation),
    JsonDiscriminatorValue(CredentialsType.Rest)
], RestAuthCredentials);
export { RestAuthCredentials };
//# sourceMappingURL=restAuthCredentials.js.map